import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import { useToast } from '../ui/ToastProvider'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import MenuGuideButton from '../components/MenuGuideButton'

const STATUS_OPTIONS = [
    { value: '', label: '전체' },
    { value: 'UNUSED', label: '미사용' },
    { value: 'USED', label: '사용완료' },
    { value: 'REVOKED', label: '폐기(대체 발급 등)' },
    { value: 'EXPIRED', label: '만료' },
]

const STATUS_BADGE = {
    UNUSED: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    USED: 'bg-sky-50 text-sky-800 border-sky-200',
    REVOKED: 'bg-amber-50 text-amber-900 border-amber-200',
    EXPIRED: 'bg-slate-100 text-slate-700 border-slate-200',
}

function unwrapData(res) {
    return res?.data?.data ?? res?.data
}

const SEND_HISTORY_STATUS_OPTIONS = [
    { value: '', label: '전체' },
    { value: 'SENT', label: '발송완료' },
    { value: 'PARTIAL', label: '일부 실패' },
    { value: 'FAILED', label: '실패' },
    { value: 'STOPPED', label: '중단' },
    { value: 'ERROR', label: '오류' },
    { value: 'PENDING', label: '대기' },
    { value: 'QUEUED', label: '대기열' },
]

const SEND_HISTORY_STATUS_LABELS = {
    SENT: '발송완료',
    PARTIAL: '일부 실패',
    FAILED: '실패',
    STOPPED: '중단',
    ERROR: '오류',
    PENDING: '대기',
    QUEUED: '대기열',
}

const SEND_HISTORY_STATUS_BADGE = {
    SENT: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    PARTIAL: 'bg-amber-50 text-amber-900 border-amber-200',
    FAILED: 'bg-red-50 text-red-800 border-red-200',
    STOPPED: 'bg-amber-50 text-amber-900 border-amber-200',
    ERROR: 'bg-red-50 text-red-800 border-red-200',
    PENDING: 'bg-slate-50 text-slate-700 border-slate-200',
    QUEUED: 'bg-sky-50 text-sky-800 border-sky-200',
}

const SOURCE_CHANNEL_LABELS = {
    WINDOW: 'Windows',
    PC: 'PC',
    MOBILE_WEB: '모바일 웹',
    APP: '앱',
    SYSTEM: '시스템',
    UNKNOWN: '미분류',
}

function getLocalDateString(offsetDays = 0) {
    const d = new Date()
    d.setDate(d.getDate() + offsetDays)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

function toIsoOrEmpty(v, endOfDay = false) {
    if (!v) return ''
    const d = new Date(v)
    if (Number.isNaN(d.getTime())) return ''
    if (endOfDay) {
        d.setHours(23, 59, 59, 999)
    } else {
        d.setHours(0, 0, 0, 0)
    }
    return d.toISOString()
}

function normalizeListPayload(payload) {
    if (Array.isArray(payload?.items)) {
        const total = Number(payload.total)
        return {
            items: payload.items,
            total: Number.isFinite(total) ? total : payload.items.length,
        }
    }
    if (Array.isArray(payload?.content)) {
        const total = Number(payload.totalElements)
        return {
            items: payload.content,
            total: Number.isFinite(total) ? total : payload.content.length,
        }
    }
    return { items: [], total: 0 }
}

function textOrDash(value) {
    const s = String(value ?? '').trim()
    return s || '-'
}

function labelSendHistoryStatus(raw) {
    const code = String(raw || '').toUpperCase()
    return SEND_HISTORY_STATUS_LABELS[code] || (raw || '-')
}

function sourceChannelLabel(value) {
    const code = String(value || '').trim().toUpperCase() || 'UNKNOWN'
    return SOURCE_CHANNEL_LABELS[code] || '미분류'
}

function successRate(successCount, sentCount) {
    const success = Number(successCount)
    const total = Number(sentCount)
    if (!Number.isFinite(success) || !Number.isFinite(total) || total <= 0) return '-'
    return `${((success / total) * 100).toFixed(1)}%`
}

function parseRawMessage(row) {
    try {
        const raw = String(row?.rawMessage || row?.message || '').trim()
        if (!raw || !raw.startsWith('{')) return {}
        const parsed = JSON.parse(raw)
        return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
        return {}
    }
}

function displaySendHistoryMessage(row) {
    const direct = String(row?.messageText || '').trim()
    if (direct) return direct
    const meta = parseRawMessage(row)
    const fromMeta = String(meta.message || '').trim()
    if (fromMeta) return fromMeta
    return '-'
}

function displaySendHistoryFailure(row) {
    const message = String(row?.errorMessage || '').trim()
    if (message) return message
    const meta = parseRawMessage(row)
    const metaMessage = String(meta?.errorMessage || '').trim()
    if (metaMessage) return metaMessage
    const code = String(row?.errorCode || meta?.errorCode || '').trim()
    return code || '-'
}

function shouldShowSendHistoryFailure(row, statusCode) {
    const failCount = Number(row?.failCount)
    if (Number.isFinite(failCount) && failCount > 0) return true
    return ['PARTIAL', 'FAILED', 'ERROR', 'STOPPED'].includes(String(statusCode || '').toUpperCase())
}

export default function OpsKakaoTalkManagementPage() {
    const { hasAnyRole } = useAuth()
    const isOperator = hasAnyRole ? hasAnyRole([Roles.OPERATOR]) : false
    const { show } = useToast()

    const [tab, setTab] = useState('history')

    const [programCode, setProgramCode] = useState('KAKAO_SENDER')
    const [orgIdFilter, setOrgIdFilter] = useState('')
    const [status, setStatus] = useState('')
    const [page, setPage] = useState(0)
    const [size] = useState(30)
    const [loading, setLoading] = useState(false)
    const [listPage, setListPage] = useState(null)

    const [orgs, setOrgs] = useState([])
    const [orgQuery, setOrgQuery] = useState('')
    const [issueOrgId, setIssueOrgId] = useState('')
    const [issueProgramCode, setIssueProgramCode] = useState('KAKAO_SENDER')
    const [issuing, setIssuing] = useState(false)
    const [lastIssued, setLastIssued] = useState(null)
    const [diagnosticProgramCode, setDiagnosticProgramCode] = useState('KAKAO_SENDER')
    const [diagnosticOrgIdFilter, setDiagnosticOrgIdFilter] = useState('')
    const [diagnosticKeyword, setDiagnosticKeyword] = useState('')
    const [diagnosticPage, setDiagnosticPage] = useState(0)
    const [diagnosticSize] = useState(20)
    const [diagnosticLoading, setDiagnosticLoading] = useState(false)
    const [diagnosticListPage, setDiagnosticListPage] = useState(null)
    const [diagnosticSelected, setDiagnosticSelected] = useState(null)
    const [diagnosticDetailLoading, setDiagnosticDetailLoading] = useState(false)

    const [sendHistoryQ, setSendHistoryQ] = useState('')
    const [sendHistoryStatus, setSendHistoryStatus] = useState('')
    const [sendHistoryFrom, setSendHistoryFrom] = useState(getLocalDateString(-1))
    const [sendHistoryTo, setSendHistoryTo] = useState(getLocalDateString(0))
    const [sendHistoryPageIndex, setSendHistoryPageIndex] = useState(0)
    const [sendHistorySize] = useState(20)
    const [sendHistoryLoading, setSendHistoryLoading] = useState(false)
    const [sendHistoryPage, setSendHistoryPage] = useState({ items: [], total: 0 })
    const [sendHistorySelected, setSendHistorySelected] = useState(null)

    const loadList = useCallback(async () => {
        setLoading(true)
        try {
            const params = {
                page,
                size,
            }
            if (programCode.trim()) params.programCode = programCode.trim()
            const oid = orgIdFilter.trim()
            if (oid && /^\d+$/.test(oid)) params.orgId = Number(oid)
            if (status) params.status = status

            const res = await api.get('/admin/external/install-codes', { params })
            const data = unwrapData(res)
            setListPage(data)
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            show('목록을 불러오지 못했습니다: ' + msg, 'error')
            setListPage(null)
        } finally {
            setLoading(false)
        }
    }, [page, size, programCode, orgIdFilter, status, show])

    useEffect(() => {
        if (!isOperator || tab !== 'history') return
        loadList()
    }, [isOperator, tab, loadList])

    const loadOrgs = useCallback(async () => {
        try {
            const res = await api.get('/orgs/options', { params: { scope: 'all' } })
            const rows = unwrapData(res)
            setOrgs(Array.isArray(rows) ? rows : [])
        } catch (e) {
            console.error(e)
            show('조직 목록을 불러오지 못했습니다.', 'error')
        }
    }, [show])

    useEffect(() => {
        if (!isOperator || tab !== 'issue') return
        loadOrgs()
    }, [isOperator, tab, loadOrgs])

    const loadDiagnosticReports = useCallback(async () => {
        setDiagnosticLoading(true)
        try {
            const params = {
                page: diagnosticPage,
                size: diagnosticSize,
            }
            if (diagnosticProgramCode.trim()) params.programCode = diagnosticProgramCode.trim()
            const oid = diagnosticOrgIdFilter.trim()
            if (oid && /^\d+$/.test(oid)) params.orgId = Number(oid)
            if (diagnosticKeyword.trim()) params.keyword = diagnosticKeyword.trim()

            const res = await api.get('/admin/external/diagnostic-reports', { params })
            setDiagnosticListPage(unwrapData(res))
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            show('진단 리포트를 불러오지 못했습니다: ' + msg, 'error')
            setDiagnosticListPage(null)
            setDiagnosticSelected(null)
        } finally {
            setDiagnosticLoading(false)
        }
    }, [diagnosticPage, diagnosticSize, diagnosticProgramCode, diagnosticOrgIdFilter, diagnosticKeyword, show])

    useEffect(() => {
        if (!isOperator || tab !== 'diagnostics') return
        loadDiagnosticReports()
    }, [isOperator, tab, loadDiagnosticReports])

    const loadSendHistory = useCallback(async () => {
        setSendHistoryLoading(true)
        try {
            const params = {
                page: sendHistoryPageIndex + 1,
                size: sendHistorySize,
                channel: 'KAKAO_BULK',
            }
            if (sendHistoryQ.trim()) params.q = sendHistoryQ.trim()
            if (sendHistoryStatus) params.status = sendHistoryStatus
            if (sendHistoryFrom) params.from = toIsoOrEmpty(sendHistoryFrom, false)
            if (sendHistoryTo) params.to = toIsoOrEmpty(sendHistoryTo, true)

            const res = await api.get('/notice-logs', { params })
            const data = normalizeListPayload(unwrapData(res))
            setSendHistoryPage(data)
            setSendHistorySelected((selected) => {
                if (!selected?.id) return selected
                return data.items.some((row) => row.id === selected.id) ? selected : null
            })
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            show('발송 이력을 불러오지 못했습니다: ' + msg, 'error')
            setSendHistoryPage({ items: [], total: 0 })
            setSendHistorySelected(null)
        } finally {
            setSendHistoryLoading(false)
        }
    }, [sendHistoryPageIndex, sendHistorySize, sendHistoryQ, sendHistoryStatus, sendHistoryFrom, sendHistoryTo, show])

    useEffect(() => {
        if (!isOperator || tab !== 'sendHistory') return
        loadSendHistory()
    }, [isOperator, tab, loadSendHistory])

    const openDiagnosticReport = async (row) => {
        if (!row?.id) return
        setDiagnosticDetailLoading(true)
        try {
            const res = await api.get(`/admin/external/diagnostic-reports/${row.id}`)
            setDiagnosticSelected(unwrapData(res))
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            show('진단 리포트 상세를 불러오지 못했습니다: ' + msg, 'error')
        } finally {
            setDiagnosticDetailLoading(false)
        }
    }

    const filteredOrgs = useMemo(() => {
        const q = orgQuery.trim().toLowerCase()
        const base = Array.isArray(orgs) ? orgs : []
        if (!q) return base.slice(0, 80)
        return base.filter((o) => String(o.name || '').toLowerCase().includes(q)).slice(0, 120)
    }, [orgs, orgQuery])

    const onIssue = async () => {
        const oid = String(issueOrgId || '').trim()
        if (!oid || !/^\d+$/.test(oid)) {
            show('조직을 선택하거나 조직 ID를 입력해 주세요.', 'error')
            return
        }
        setIssuing(true)
        setLastIssued(null)
        try {
            const res = await api.post('/admin/external/install-codes/issue', {
                programCode: issueProgramCode.trim() || 'KAKAO_SENDER',
                orgId: Number(oid),
            })
            const data = unwrapData(res)
            setLastIssued(data)
            show('설치코드가 발급되었습니다. 평문은 이 화면에서만 확인할 수 있습니다.', 'success')
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            show('발급 실패: ' + msg, 'error')
        } finally {
            setIssuing(false)
        }
    }

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text)
            show('복사했습니다', 'success')
        } catch {
            show('복사에 실패했습니다', 'error')
        }
    }

    if (!isOperator) {
        return <div className="p-4">운영자 권한이 필요합니다.</div>
    }

    const items = listPage?.content || []
    const totalPages = listPage?.totalPages ?? 0
    const diagnosticItems = diagnosticListPage?.content || []
    const diagnosticTotalPages = diagnosticListPage?.totalPages ?? 0
    const sendHistoryItems = Array.isArray(sendHistoryPage.items) ? sendHistoryPage.items : []
    const sendHistoryTotal = Number(sendHistoryPage.total) || 0
    const sendHistoryTotalPages = Math.ceil(sendHistoryTotal / sendHistorySize)

    return (
        <section className="w-full max-w-none p-4">
            <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-900">카카오톡 관리</h1>
                <MenuGuideButton slug="ops-management" title="운영관리 가이드" />
            </div>

            <div className="mt-6 flex gap-1 border-b border-slate-200">
                <button
                    type="button"
                    className={`px-4 py-2 text-sm font-medium rounded-t-md border border-b-0 ${
                        tab === 'history'
                            ? 'bg-white border-slate-200 text-slate-900 -mb-px'
                            : 'bg-slate-50 border-transparent text-slate-600 hover:text-slate-900'
                    }`}
                    onClick={() => setTab('history')}
                >
                    설치코드 이력
                </button>
                <button
                    type="button"
                    className={`px-4 py-2 text-sm font-medium rounded-t-md border border-b-0 ${
                        tab === 'issue'
                            ? 'bg-white border-slate-200 text-slate-900 -mb-px'
                            : 'bg-slate-50 border-transparent text-slate-600 hover:text-slate-900'
                    }`}
                    onClick={() => setTab('issue')}
                >
                    설치코드 발급
                </button>
                <button
                    type="button"
                    className={`px-4 py-2 text-sm font-medium rounded-t-md border border-b-0 ${
                        tab === 'sendHistory'
                            ? 'bg-white border-slate-200 text-slate-900 -mb-px'
                            : 'bg-slate-50 border-transparent text-slate-600 hover:text-slate-900'
                    }`}
                    onClick={() => setTab('sendHistory')}
                >
                    발송 이력
                </button>
                <button
                    type="button"
                    className={`px-4 py-2 text-sm font-medium rounded-t-md border border-b-0 ${
                        tab === 'diagnostics'
                            ? 'bg-white border-slate-200 text-slate-900 -mb-px'
                            : 'bg-slate-50 border-transparent text-slate-600 hover:text-slate-900'
                    }`}
                    onClick={() => setTab('diagnostics')}
                >
                    진단리포트
                </button>
            </div>

            <div className="rounded-b-lg border border-t-0 border-slate-200 bg-white p-4">
                {tab === 'history' && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-3 items-end">
                            <label className="flex flex-col text-xs text-slate-600">
                                프로그램 코드
                                <input
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-44"
                                    value={programCode}
                                    onChange={(e) => { setProgramCode(e.target.value); setPage(0) }}
                                    placeholder="KAKAO_SENDER"
                                />
                            </label>
                            <label className="flex flex-col text-xs text-slate-600">
                                조직 ID
                                <input
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-28"
                                    value={orgIdFilter}
                                    onChange={(e) => { setOrgIdFilter(e.target.value); setPage(0) }}
                                    placeholder="선택"
                                />
                            </label>
                            <label className="flex flex-col text-xs text-slate-600">
                                상태
                                <select
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-44"
                                    value={status}
                                    onChange={(e) => { setStatus(e.target.value); setPage(0) }}
                                >
                                    {STATUS_OPTIONS.map((o) => (
                                        <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </label>
                            <button
                                type="button"
                                className="rounded bg-slate-800 text-white text-sm px-3 py-2 hover:bg-slate-700"
                                onClick={() => loadList()}
                                disabled={loading}
                            >
                                조회
                            </button>
                        </div>

                        <div className="overflow-x-auto border rounded-md">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-left text-xs text-slate-600">
                                    <tr>
                                        <th className="px-2 py-2 whitespace-nowrap">ID</th>
                                        <th className="px-2 py-2 whitespace-nowrap">프로그램</th>
                                        <th className="px-2 py-2 whitespace-nowrap">조직</th>
                                        <th className="px-2 py-2 whitespace-nowrap">발급자</th>
                                        <th className="px-2 py-2 whitespace-nowrap">상태</th>
                                        <th className="px-2 py-2 whitespace-nowrap">코드 지문</th>
                                        <th className="px-2 py-2 whitespace-nowrap">등록 클라이언트</th>
                                        <th className="px-2 py-2 whitespace-nowrap">기기명</th>
                                        <th className="px-2 py-2 whitespace-nowrap">자격증명</th>
                                        <th className="px-2 py-2 whitespace-nowrap">생성</th>
                                        <th className="px-2 py-2 whitespace-nowrap">만료</th>
                                        <th className="px-2 py-2 whitespace-nowrap">사용 시각</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && (
                                        <tr>
                                            <td colSpan={12} className="px-3 py-6 text-center text-slate-500">불러오는 중…</td>
                                        </tr>
                                    )}
                                    {!loading && items.length === 0 && (
                                        <tr>
                                            <td colSpan={12} className="px-3 py-6 text-center text-slate-500">데이터가 없습니다.</td>
                                        </tr>
                                    )}
                                    {!loading && items.map((row) => (
                                        <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50/80">
                                            <td className="px-2 py-2 whitespace-nowrap font-mono text-xs">{row.id}</td>
                                            <td className="px-2 py-2 whitespace-nowrap">{row.programCode}</td>
                                            <td className="px-2 py-2">
                                                <div className="max-w-[180px] truncate" title={row.orgName}>{row.orgName || '-'}</div>
                                                <div className="text-xs text-slate-500">{row.orgId != null ? `#${row.orgId}` : ''}</div>
                                            </td>
                                            <td className="px-2 py-2">
                                                <div className="max-w-[160px] truncate" title={row.issuedByEmail}>{row.issuedByEmail || '-'}</div>
                                                {row.issuedByName && (
                                                    <div className="text-xs text-slate-500 truncate max-w-[160px]">{row.issuedByName}</div>
                                                )}
                                            </td>
                                            <td className="px-2 py-2 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-0.5 rounded border text-xs ${STATUS_BADGE[row.status] || 'bg-slate-50 border-slate-200'}`}>
                                                    {STATUS_OPTIONS.find((s) => s.value === row.status)?.label || row.status}
                                                </span>
                                            </td>
                                            <td className="px-2 py-2 font-mono text-xs whitespace-nowrap">{row.codeFingerprint}</td>
                                            <td className="px-2 py-2 font-mono text-xs max-w-[140px] truncate" title={row.usedClientId}>{row.usedClientId || '-'}</td>
                                            <td className="px-2 py-2 max-w-[120px] truncate" title={row.usedDeviceName}>{row.usedDeviceName || '-'}</td>
                                            <td className="px-2 py-2 text-xs whitespace-nowrap">
                                                {row.credentialId != null
                                                    ? (row.credentialActive ? '활성' : '비활성')
                                                    : '-'}
                                            </td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs">{formatDateTimeMinute(row.createdAt)}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs">{formatDateTimeMinute(row.expiresAt)}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs">{formatDateTimeMinute(row.usedAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="flex items-center gap-2 text-sm">
                                <button
                                    type="button"
                                    className="rounded border px-2 py-1 disabled:opacity-40"
                                    disabled={page <= 0}
                                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                                >
                                    이전
                                </button>
                                <span className="text-slate-600">
                                    {page + 1} / {totalPages} (총 {listPage?.totalElements ?? 0}건)
                                </span>
                                <button
                                    type="button"
                                    className="rounded border px-2 py-1 disabled:opacity-40"
                                    disabled={page >= totalPages - 1}
                                    onClick={() => setPage((p) => p + 1)}
                                >
                                    다음
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {tab === 'issue' && (
                    <div className="space-y-4 max-w-lg">
                        <p className="text-sm text-slate-600">
                            동일 프로그램·동일 조직에 이미 유효한 미사용 코드가 있으면 새로 발급 시 기존 코드는 자동으로 폐기됩니다.
                            (일반 사용자 화면에서의 발급과 같은 규칙입니다.)
                        </p>
                        <label className="flex flex-col text-xs text-slate-600">
                            프로그램 코드
                            <input
                                className="mt-1 rounded border px-2 py-1.5 text-sm"
                                value={issueProgramCode}
                                onChange={(e) => setIssueProgramCode(e.target.value)}
                            />
                        </label>
                        <label className="flex flex-col text-xs text-slate-600">
                            조직 검색
                            <input
                                className="mt-1 rounded border px-2 py-1.5 text-sm"
                                value={orgQuery}
                                onChange={(e) => setOrgQuery(e.target.value)}
                                placeholder="조직명 일부"
                            />
                        </label>
                        <label className="flex flex-col text-xs text-slate-600">
                            대상 조직
                            <select
                                className="mt-1 rounded border px-2 py-1.5 text-sm"
                                value={issueOrgId}
                                onChange={(e) => setIssueOrgId(e.target.value)}
                            >
                                <option value="">선택…</option>
                                {filteredOrgs.map((o) => (
                                    <option key={o.id} value={o.id}>
                                        [{o.id}] {o.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button
                            type="button"
                            className="rounded bg-brand-sky text-white text-sm px-4 py-2 hover:opacity-90 disabled:opacity-50"
                            onClick={onIssue}
                            disabled={issuing}
                        >
                            {issuing ? '발급 중…' : '설치코드 발급'}
                        </button>

                        {lastIssued && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm space-y-2">
                                <div className="font-semibold text-amber-950">발급 완료 — 평문은 재조회할 수 없습니다</div>
                                <div>조직: {lastIssued.orgName} (#{lastIssued.orgId})</div>
                                <div>만료: {formatDateTimeMinute(lastIssued.expiresAt)} (약 30분)</div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-mono font-semibold tracking-wide">{lastIssued.installCode}</span>
                                    <button
                                        type="button"
                                        className="text-xs underline text-amber-900"
                                        onClick={() => copyText(lastIssued.installCode)}
                                    >
                                        복사
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}



                {tab === 'sendHistory' && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-3 items-end">
                            <label className="flex flex-col text-xs text-slate-600">
                                검색어
                                <input
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-64"
                                    value={sendHistoryQ}
                                    onChange={(e) => { setSendHistoryQ(e.target.value); setSendHistoryPageIndex(0) }}
                                    placeholder="발송자, 메시지, 요청키"
                                />
                            </label>
                            <label className="flex flex-col text-xs text-slate-600">
                                상태
                                <select
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-36"
                                    value={sendHistoryStatus}
                                    onChange={(e) => { setSendHistoryStatus(e.target.value); setSendHistoryPageIndex(0) }}
                                >
                                    {SEND_HISTORY_STATUS_OPTIONS.map((o) => (
                                        <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="flex flex-col text-xs text-slate-600">
                                시작일
                                <input
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-40"
                                    type="date"
                                    value={sendHistoryFrom}
                                    onChange={(e) => { setSendHistoryFrom(e.target.value); setSendHistoryPageIndex(0) }}
                                />
                            </label>
                            <label className="flex flex-col text-xs text-slate-600">
                                종료일
                                <input
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-40"
                                    type="date"
                                    value={sendHistoryTo}
                                    onChange={(e) => { setSendHistoryTo(e.target.value); setSendHistoryPageIndex(0) }}
                                />
                            </label>
                            <button
                                type="button"
                                className="rounded bg-slate-800 text-white text-sm px-3 py-2 hover:bg-slate-700 disabled:opacity-50"
                                onClick={() => loadSendHistory()}
                                disabled={sendHistoryLoading}
                            >
                                조회
                            </button>
                        </div>

                        <div className="overflow-x-auto border rounded-md">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-left text-xs text-slate-600">
                                    <tr>
                                        <th className="px-2 py-2 whitespace-nowrap">발송 시각</th>
                                        <th className="px-2 py-2 whitespace-nowrap">상태</th>
                                        <th className="px-2 py-2 whitespace-nowrap">발송자</th>
                                        <th className="px-2 py-2 whitespace-nowrap">발송자 ID</th>
                                        <th className="px-2 py-2 whitespace-nowrap">발송대상</th>
                                        <th className="px-2 py-2 whitespace-nowrap text-right">건수</th>
                                        <th className="px-2 py-2 whitespace-nowrap text-right">성공</th>
                                        <th className="px-2 py-2 whitespace-nowrap text-right">실패</th>
                                        <th className="px-2 py-2 whitespace-nowrap">실패 사유</th>
                                        <th className="px-2 py-2 whitespace-nowrap text-right">성공률</th>
                                        <th className="px-2 py-2 whitespace-nowrap">이미지</th>
                                        <th className="px-2 py-2 whitespace-nowrap">템플릿</th>
                                        <th className="px-2 py-2 whitespace-nowrap">상세</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sendHistoryLoading && (
                                        <tr>
                                            <td colSpan={13} className="px-3 py-6 text-center text-slate-500">불러오는 중…</td>
                                        </tr>
                                    )}
                                    {!sendHistoryLoading && sendHistoryItems.length === 0 && (
                                        <tr>
                                            <td colSpan={13} className="px-3 py-6 text-center text-slate-500">데이터가 없습니다.</td>
                                        </tr>
                                    )}
                                    {!sendHistoryLoading && sendHistoryItems.map((row) => {
                                        const statusCode = String(row.status || '').toUpperCase()
                                        const failureText = displaySendHistoryFailure(row)
                                        const showFailure = shouldShowSendHistoryFailure(row, statusCode)
                                        return (
                                            <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50/80">
                                                <td className="px-2 py-2 whitespace-nowrap text-xs">{row.createdAtDisplay || formatDateTimeMinute(row.createdAt)}</td>
                                                <td className="px-2 py-2 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-0.5 rounded border text-xs ${SEND_HISTORY_STATUS_BADGE[statusCode] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                                                        {labelSendHistoryStatus(row.status)}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-2 max-w-[120px] truncate" title={row.senderName}>{textOrDash(row.senderName)}</td>
                                                <td className="px-2 py-2 max-w-[120px] truncate font-mono text-xs" title={row.senderId}>{textOrDash(row.senderId)}</td>
                                                <td className="px-2 py-2 max-w-[160px] truncate" title={row.recipientGroupName}>{textOrDash(row.recipientGroupName)}</td>
                                                <td className="px-2 py-2 whitespace-nowrap text-right">{row.sentCount ?? '-'}</td>
                                                <td className="px-2 py-2 whitespace-nowrap text-right text-emerald-700">{row.successCount ?? '-'}</td>
                                                <td className="px-2 py-2 whitespace-nowrap text-right text-red-700">{row.failCount ?? '-'}</td>
                                                <td className="px-2 py-2 max-w-[260px] truncate text-xs text-slate-700" title={showFailure ? failureText : ''}>{showFailure ? failureText : '-'}</td>
                                                <td className="px-2 py-2 whitespace-nowrap text-right">{successRate(row.successCount, row.sentCount)}</td>
                                                <td className="px-2 py-2 whitespace-nowrap">{row.imageIncluded ? 'O' : 'X'}</td>
                                                <td className="px-2 py-2 max-w-[120px] truncate" title={row.templateCode || row.templateId}>{row.templateCode || row.templateId || '-'}</td>
                                                <td className="px-2 py-2 whitespace-nowrap">
                                                    <button
                                                        type="button"
                                                        className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                                                        onClick={() => setSendHistorySelected(row)}
                                                    >
                                                        보기
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                            <div className="text-slate-600">총 {sendHistoryTotal}건</div>
                            {sendHistoryTotalPages > 1 && (
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="rounded border px-2 py-1 disabled:opacity-40"
                                        disabled={sendHistoryPageIndex <= 0}
                                        onClick={() => setSendHistoryPageIndex((p) => Math.max(0, p - 1))}
                                    >
                                        이전
                                    </button>
                                    <span className="text-slate-600">
                                        {sendHistoryPageIndex + 1} / {sendHistoryTotalPages}
                                    </span>
                                    <button
                                        type="button"
                                        className="rounded border px-2 py-1 disabled:opacity-40"
                                        disabled={sendHistoryPageIndex >= sendHistoryTotalPages - 1}
                                        onClick={() => setSendHistoryPageIndex((p) => p + 1)}
                                    >
                                        다음
                                    </button>
                                </div>
                            )}
                        </div>

                        {sendHistorySelected && (
                            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="font-semibold text-slate-900">발송 상세 #{sendHistorySelected.id}</div>
                                    <button
                                        type="button"
                                        className="text-xs underline text-slate-600 hover:text-slate-900"
                                        onClick={() => setSendHistorySelected(null)}
                                    >
                                        닫기
                                    </button>
                                </div>
                                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                                    <div>발송 시각: {sendHistorySelected.createdAtDisplay || formatDateTimeMinute(sendHistorySelected.createdAt)}</div>
                                    <div>상태: {labelSendHistoryStatus(sendHistorySelected.status)}</div>
                                    <div>발송자: {textOrDash(sendHistorySelected.senderName)}</div>
                                    <div>발송자 ID: {textOrDash(sendHistorySelected.senderId)}</div>
                                    <div>발송대상: {textOrDash(sendHistorySelected.recipientGroupName)}</div>
                                    <div>발송 경로: {sourceChannelLabel(sendHistorySelected.sourceChannel)}</div>
                                    <div>건수: 총 {sendHistorySelected.sentCount ?? '-'} / 성공 {sendHistorySelected.successCount ?? '-'} / 실패 {sendHistorySelected.failCount ?? '-'}</div>
                                    <div>이미지: {sendHistorySelected.imageIncluded ? '포함' : '없음'}</div>
                                    <div>템플릿: {sendHistorySelected.templateCode || sendHistorySelected.templateId || '-'}</div>
                                    <div className="break-all">요청키: {textOrDash(sendHistorySelected.externalRequestId)}</div>
                                    <div className="sm:col-span-2">실패 사유: {textOrDash(displaySendHistoryFailure(sendHistorySelected))}</div>
                                    <div>오류 코드: {textOrDash(sendHistorySelected.errorCode)}</div>
                                    <div>시도: {sendHistorySelected.attemptCount ?? '-'}</div>
                                </div>
                                <div className="mt-3">
                                    <div className="text-xs font-semibold text-slate-500 mb-1">메시지</div>
                                    <div className="rounded border border-slate-200 bg-white px-3 py-2 whitespace-pre-wrap text-slate-800">{displaySendHistoryMessage(sendHistorySelected)}</div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {tab === 'diagnostics' && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-3 items-end">
                            <label className="flex flex-col text-xs text-slate-600">
                                프로그램 코드
                                <input
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-44"
                                    value={diagnosticProgramCode}
                                    onChange={(e) => { setDiagnosticProgramCode(e.target.value); setDiagnosticPage(0) }}
                                    placeholder="KAKAO_SENDER"
                                />
                            </label>
                            <label className="flex flex-col text-xs text-slate-600">
                                조직 ID
                                <input
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-28"
                                    value={diagnosticOrgIdFilter}
                                    onChange={(e) => { setDiagnosticOrgIdFilter(e.target.value); setDiagnosticPage(0) }}
                                    placeholder="선택"
                                />
                            </label>
                            <label className="flex flex-col text-xs text-slate-600">
                                검색어
                                <input
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-64"
                                    value={diagnosticKeyword}
                                    onChange={(e) => { setDiagnosticKeyword(e.target.value); setDiagnosticPage(0) }}
                                    placeholder="clientId, 메시지, 리포트 내용"
                                />
                            </label>
                            <button
                                type="button"
                                className="rounded bg-slate-800 text-white text-sm px-3 py-2 hover:bg-slate-700"
                                onClick={() => loadDiagnosticReports()}
                                disabled={diagnosticLoading}
                            >
                                조회
                            </button>
                        </div>

                        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)]">
                            <div className="overflow-x-auto border rounded-md">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-slate-50 text-left text-xs text-slate-600">
                                        <tr>
                                            <th className="px-2 py-2 whitespace-nowrap">수신 시각</th>
                                            <th className="px-2 py-2 whitespace-nowrap">조직</th>
                                            <th className="px-2 py-2 whitespace-nowrap">클라이언트</th>
                                            <th className="px-2 py-2 whitespace-nowrap">출처</th>
                                            <th className="px-2 py-2 whitespace-nowrap">메시지</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {diagnosticLoading && (
                                            <tr>
                                                <td colSpan={5} className="px-3 py-6 text-center text-slate-500">불러오는 중…</td>
                                            </tr>
                                        )}
                                        {!diagnosticLoading && diagnosticItems.length === 0 && (
                                            <tr>
                                                <td colSpan={5} className="px-3 py-6 text-center text-slate-500">데이터가 없습니다.</td>
                                            </tr>
                                        )}
                                        {!diagnosticLoading && diagnosticItems.map((row) => (
                                            <tr
                                                key={row.id}
                                                className={`border-t border-slate-100 hover:bg-slate-50/80 cursor-pointer ${diagnosticSelected?.id === row.id ? 'bg-sky-50/70' : ''}`}
                                                onClick={() => openDiagnosticReport(row)}
                                            >
                                                <td className="px-2 py-2 whitespace-nowrap text-xs">{formatDateTimeMinute(row.createdAt)}</td>
                                                <td className="px-2 py-2">
                                                    <div className="max-w-[160px] truncate" title={row.orgName}>{row.orgName || '-'}</div>
                                                    <div className="text-xs text-slate-500">{row.orgId != null ? `#${row.orgId}` : ''}</div>
                                                </td>
                                                <td className="px-2 py-2 font-mono text-xs max-w-[150px] truncate" title={row.clientId}>{row.clientId || '-'}</td>
                                                <td className="px-2 py-2 text-xs max-w-[150px] truncate" title={row.source}>{row.source || '-'}</td>
                                                <td className="px-2 py-2 max-w-[260px] truncate" title={row.message}>{row.message || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                                {!diagnosticSelected && !diagnosticDetailLoading && (
                                    <div className="py-10 text-center text-sm text-slate-500">
                                        목록에서 리포트를 선택해 주세요.
                                    </div>
                                )}
                                {diagnosticDetailLoading && (
                                    <div className="py-10 text-center text-sm text-slate-500">상세를 불러오는 중…</div>
                                )}
                                {diagnosticSelected && !diagnosticDetailLoading && (
                                    <div className="space-y-3">
                                        <div className="text-sm font-semibold text-slate-900">진단 리포트 #{diagnosticSelected.id}</div>
                                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                                            <div>수신: {formatDateTimeMinute(diagnosticSelected.createdAt)}</div>
                                            <div>프로그램: {diagnosticSelected.programCode || '-'}</div>
                                            <div>조직: {diagnosticSelected.orgName || '-'}</div>
                                            <div>클라이언트: {diagnosticSelected.clientId || '-'}</div>
                                            <div className="col-span-2 truncate" title={diagnosticSelected.userAgent}>환경: {diagnosticSelected.userAgent || '-'}</div>
                                        </div>
                                        <pre className="max-h-[520px] overflow-auto whitespace-pre-wrap rounded bg-white p-3 text-xs leading-5 text-slate-800 border border-slate-200">
                                            {diagnosticSelected.reportBody || diagnosticSelected.bodyPreview || ''}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </div>

                        {diagnosticTotalPages > 1 && (
                            <div className="flex items-center gap-2 text-sm">
                                <button
                                    type="button"
                                    className="rounded border px-2 py-1 disabled:opacity-40"
                                    disabled={diagnosticPage <= 0}
                                    onClick={() => setDiagnosticPage((p) => Math.max(0, p - 1))}
                                >
                                    이전
                                </button>
                                <span className="text-slate-600">
                                    {diagnosticPage + 1} / {diagnosticTotalPages} (총 {diagnosticListPage?.totalElements ?? 0}건)
                                </span>
                                <button
                                    type="button"
                                    className="rounded border px-2 py-1 disabled:opacity-40"
                                    disabled={diagnosticPage >= diagnosticTotalPages - 1}
                                    onClick={() => setDiagnosticPage((p) => p + 1)}
                                >
                                    다음
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    )
}
